// Ideal-customer fit: the criteria of a profile, the AI prompt and answer schema, and the overall score.
import { z } from "zod";

import type { Fit, FitReason, IdealCustomer } from "../types";
import { UNTRUSTED_NOTE, untrusted } from "./ai";

export type FitResource = "companies" | "contacts";
export type Criterion = { id: string; label: string; kind: "good" | "bad" };

const lines = (items?: string[]) =>
  (items ?? []).map((item) => item.trim()).filter(Boolean);

/** Good-fit signals (with the target role, geography and size) and bad-fit signals, each with a short id */
export const criteriaOf = (
  profile: IdealCustomer,
  resource: FitResource,
): Criterion[] => {
  const good = [
    ...lines(profile.positive_criteria),
    ...(resource === "contacts" && profile.target_role?.trim()
      ? [`Role: ${profile.target_role.trim()}`]
      : []),
    ...(profile.target_geography?.trim()
      ? [`Geography: ${profile.target_geography.trim()}`]
      : []),
    ...(profile.target_company_size?.trim()
      ? [`Company size: ${profile.target_company_size.trim()}`]
      : []),
  ];
  return [
    ...good.map((label, i) => ({
      id: `g${i + 1}`,
      label,
      kind: "good" as const,
    })),
    ...lines(profile.negative_criteria).map((label, i) => ({
      id: `b${i + 1}`,
      label,
      kind: "bad" as const,
    })),
  ];
};

export const FitAnswerSchema = z.object({
  scores: z.array(
    z.object({
      id: z.string(),
      score: z.number().int().min(0).max(5).nullable(),
      reasoning: z.string().max(400),
    }),
  ),
});
export type FitAnswer = z.infer<typeof FitAnswerSchema>;

const MAX_RECORD_CHARS = 20_000;

export const buildFitPrompt = (
  profile: IdealCustomer,
  criteria: Criterion[],
  resource: FitResource,
  record: Record<string, unknown>,
) => {
  const what = resource === "companies" ? "company" : "contact";
  return [
    `Score how well this ${what} fits the ideal customer profile of a B2B seller.`,
    "For each criterion give a score from 0 to 5 and a one-line reasoning (under 200 characters).",
    "Good-fit criteria (g…): 0 = not at all, 5 = fully matches. Bad-fit criteria (b…): 0 = absent, 5 = clearly present.",
    "When the data says nothing about a criterion, give score null and say so.",
    'Answer with JSON only: {"scores": [{"id": "g1", "score": 4, "reasoning": "..."}]}',
    "",
    "Ideal customer profile:",
    `- Name: ${profile.name}`,
    profile.offer && `- What we sell: ${profile.offer}`,
    profile.customer_problem &&
      `- The customer's problem: ${profile.customer_problem}`,
    lines(profile.example_clients).length > 0 &&
      `- Example clients: ${lines(profile.example_clients).join(", ")}`,
    "",
    "Criteria:",
    ...criteria.map((c) => `- ${c.id} (${c.kind}-fit): ${c.label}`),
    "",
    UNTRUSTED_NOTE,
    untrusted(what, JSON.stringify(record, null, 1).slice(0, MAX_RECORD_CHARS)),
  ]
    .filter((line) => line !== false && line !== undefined && line !== "")
    .join("\n");
};

/**
 * The overall 0-5 score: the mean of the good-fit scores, minus half the strongest bad-fit signal
 * (a clear red flag, 5, costs 2.5 points). Unknown (null) scores are left out.
 * Match: 3 strong (4-5), 2 partial (2-3), 1 weak (0-1). Null when nothing could be scored.
 */
export const overallFit = (
  reasons: Record<string, FitReason>,
): Pick<Fit, "fit_score" | "fit_match"> | null => {
  const known = Object.values(reasons).filter((r) => r.score !== null);
  const good = known.filter((r) => r.kind === "good").map((r) => r.score!);
  const bad = known.filter((r) => r.kind === "bad").map((r) => r.score!);
  if (!good.length) return null;
  const mean = good.reduce((a, b) => a + b, 0) / good.length;
  const score = Math.min(
    5,
    Math.max(0, Math.round(mean - Math.max(0, ...bad) / 2)),
  );
  return {
    fit_score: score,
    fit_match: score >= 4 ? 3 : score >= 2 ? 2 : 1,
  };
};

/** The AI's answer keyed by criterion label, keeping only the criteria that were asked */
export const reasonsFrom = (criteria: Criterion[], answer: FitAnswer) => {
  const reasons: Record<string, FitReason> = {};
  for (const { id, score, reasoning } of answer.scores) {
    const criterion = criteria.find((c) => c.id === id);
    if (criterion)
      reasons[criterion.label] = { score, reasoning, kind: criterion.kind };
  }
  return reasons;
};

/** The record fields worth showing the model (no ids, files or empty values) */
export const fitData = (resource: FitResource, record: Record<string, any>) => {
  const keys =
    resource === "companies"
      ? [
          "name",
          "sector",
          "size",
          "website",
          "linkedin_url",
          "city",
          "state_abbr",
          "country",
          "description",
          "revenue",
        ]
      : [
          "first_name",
          "last_name",
          "title",
          "company_name",
          "background",
          "linkedin_url",
          "status",
        ];
  const data: Record<string, unknown> = {};
  for (const key of keys)
    if (record[key] !== null && record[key] !== undefined && record[key] !== "")
      data[key] = record[key];
  if (resource === "contacts" && Array.isArray(record.email_jsonb))
    data.email_domains = [
      ...new Set(
        record.email_jsonb.map((e: { email: string }) => e.email.split("@")[1]),
      ),
    ];
  return data;
};

export const FIT_LIST_FIELDS = [
  "positive_criteria",
  "negative_criteria",
  "example_clients",
] as const;

/** Before saving: trimmed lines, no empty items, no unnamed profile */
export const cleanIdealCustomers = (profiles?: IdealCustomer[]) =>
  (profiles ?? [])
    .filter((p) => p?.name?.trim())
    .map((p) => {
      const clean: IdealCustomer = { ...p, name: p.name.trim() };
      for (const field of FIT_LIST_FIELDS)
        clean[field] = (p[field] ?? []).map((s) => s.trim()).filter(Boolean);
      return clean;
    });
